import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Chip,
  Skeleton,
  Alert,
  Divider,
  Tooltip,
  IconButton,
  useTheme,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  CircularProgress,
  TextField,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  ContentCopy as DuplicateIcon,
  MeetingRoom as StallIcon,
  Grass as FieldIcon,
  Warning as WarningIcon,
  ExitToApp as DischargeIcon,
  AutoFixHigh as AIIcon,
} from '@mui/icons-material';
import HoofIcon from '../../components/HoofIcon';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { addDays, format, differenceInYears, parseISO } from 'date-fns';
import { useSnackbar } from 'notistack';
import { treatmentSheetApi } from '../../utils/api';
import { useRBAC } from '../../auth/useRBAC';
import { treatmentSheetControls } from '../../auth/pageControls';
import type { TreatmentSheet, MedicalAlert, OrderWithSlots, TreatmentSheetBillingSummary } from '../../types/treatmentSheet';
import { formatDollars } from '../../utils/money';
import TreatmentGrid from '../../components/TreatmentSheet/TreatmentGrid';
import VitalsPanel from '../../components/TreatmentSheet/VitalsPanel';
import ClinicalNotesSection from '../../components/TreatmentSheet/ClinicalNotesSection';
import AddOrderSlideOver from '../../components/TreatmentSheet/AddOrderSlideOver';
import AIOrderGenerator from '../../components/TreatmentSheet/AIOrderGenerator';
import DayLadder from '../../components/TreatmentSheet/DayLadder';
import { buildDayTabs, dayKeyOf } from '../../components/TreatmentSheet/gridDays';

// ─── Helpers ──────────────────────────────────────────────────────────────────

function computeAge(dateOfBirth?: string): string {
  if (!dateOfBirth) return '';
  try {
    const years = differenceInYears(new Date(), parseISO(dateOfBirth));
    return `${years} yr`;
  } catch {
    return '';
  }
}

// ─── PatientHeaderBar ─────────────────────────────────────────────────────────

interface PatientHeaderBarProps {
  sheet: TreatmentSheet;
  /** Running cost of the stay; undefined while loading or when never billed. */
  billing?: TreatmentSheetBillingSummary;
  /** Order authoring (Add Order / AI Generate) only applies to today's tab. */
  viewingToday: boolean;
  /** There is a prior day to copy forward from. */
  canDuplicate: boolean;
  onDischarge: () => void;
  onAddOrder: () => void;
  onAIGenerate: () => void;
  onDuplicateDay: () => void;
}

const PatientHeaderBar: React.FC<PatientHeaderBarProps> = ({
  sheet,
  billing,
  viewingToday,
  canDuplicate,
  onDischarge,
  onAddOrder,
  onAIGenerate,
  onDuplicateDay,
}) => {
  const theme = useTheme();
  const navigate = useNavigate();
  /**
   * TWO TERMS, AND-ed (INV-108 + decision #84). The PERMISSION is what every route behind these
   * four buttons declares — duplicate-day and add-order (treatment-order.controller.ts :45-46,
   * :39-40), AI generate (the same create route), discharge (treatment-sheet.controller.ts
   * `update` :45-46) all require `manage:medical_records`, so a person without it never sees them.
   *
   * ⚠ THE ROLE TERM IS A MIRROR, NOT A UI INVENTION — corrected 2026-09-21. It reads as UI-only to
   * a decorator walk because the rule lives in a SERVICE: `treatment-order.service.ts:30`
   * `CAN_ORDER = [VETERINARIAN, OWNER, ADMIN]`, enforced on create (:95), duplicate-day (:173),
   * update (:356) and — since the no-access fix wave — discontinue (:384). Three of this screen's
   * four buttons are therefore gated the same way by the API, and dropping the role term would
   * give a technician three buttons whose save 403s.
   *
   * What is genuinely UI-ONLY is DISCHARGE. `treatment-sheet.service.ts` has no role check at
   * all, so the API would let a technician who holds `manage:medical_records` discharge a patient;
   * this screen does not offer it. That is the one term here to argue about, and who may write
   * orders remains Ali's call (#84) — on the SERVER first, since that is where the rule is.
   */
  const rbac = useRBAC();
  const { hasRole } = rbac;
  const can = treatmentSheetControls(rbac);
  const isVet = can.orders && (hasRole('VETERINARIAN') || hasRole('OWNER') || hasRole('ADMIN'));
  const { horse, attendingVet, locationType, locationLabel, stall, admitDate, chiefComplaint, status } = sheet;

  const alerts: MedicalAlert[] = horse?.medicalAlerts ?? [];

  return (
    <Paper
      elevation={3}
      square
      sx={{
        px: 3,
        py: 1.5,
        position: 'sticky',
        top: 0,
        zIndex: theme.zIndex.appBar - 1,
        borderBottom: `1px solid ${theme.palette.divider}`,
        backgroundColor: theme.palette.background.paper,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
        {/* Back */}
        <Tooltip title="Back to Hospital Board">
          <IconButton onClick={() => navigate('/hospital')} size="small" sx={{ mt: 0.5 }}>
            <ArrowBackIcon />
          </IconButton>
        </Tooltip>

        {/* Horse info */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <Typography variant="h5" fontWeight={700} noWrap>
              {horse?.name ?? '—'}
            </Typography>
            <Chip
              label={status}
              size="small"
              color={
                status === 'ACTIVE'
                  ? 'success'
                  : status === 'DISCHARGED'
                  ? 'default'
                  : 'warning'
              }
            />
            {/* Running cost of the stay. Only rendered where it is TRUE: a clinic
                that has not switched hospital auto-bill on gets no chip at all,
                rather than a status it cannot act on. */}
            {billing?.autoBillEnabled && (
              billing.linked ? (
                <Tooltip title={`${billing.invoiceNumber ?? 'Invoice'} — charges so far, including tax. Click to open it.`}>
                  <Chip
                    label={`${formatDollars(billing.total)} billed`}
                    size="small"
                    color="info"
                    variant="outlined"
                    onClick={() => billing.invoiceId && navigate(`/billing?invoiceId=${billing.invoiceId}`)}
                  />
                </Tooltip>
              ) : (
                <Tooltip title="Treatments will be added to this admission's invoice as they are given.">
                  <Chip label="No charges yet" size="small" variant="outlined" />
                </Tooltip>
              )
            )}
            {alerts.map((alert, idx) => (
              <Tooltip key={idx} title={alert.description}>
                <Chip
                  icon={<WarningIcon />}
                  label={alert.type}
                  size="small"
                  color="error"
                  variant="outlined"
                />
              </Tooltip>
            ))}
          </Box>

          <Box sx={{ display: 'flex', gap: 3, mt: 0.5, flexWrap: 'wrap' }}>
            <Typography variant="body2" color="text.secondary">
              {horse?.breed ?? ''}
              {horse?.dateOfBirth ? ` · ${computeAge(horse.dateOfBirth)}` : ''}
              {horse?.gender ? ` · ${horse.gender}` : ''}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Vet: <strong>{attendingVet?.name ?? '—'}</strong>
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Admitted:{' '}
              <strong>
                {admitDate ? format(parseISO(admitDate), 'MMM d, yyyy') : '—'}
              </strong>
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              {locationType === 'STALL' ? (
                <StallIcon sx={{ fontSize: 14 }} />
              ) : (
                <FieldIcon sx={{ fontSize: 14 }} />
              )}
              {locationType === 'STALL'
                ? stall?.name ?? 'Unknown Stall'
                : locationLabel ?? 'Field'}
            </Typography>
          </Box>

          {chiefComplaint && (
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.25, display: 'block' }}>
              Chief Complaint: {chiefComplaint}
            </Typography>
          )}
        </Box>

        {/* Actions */}
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexShrink: 0 }}>
          {isVet && status === 'ACTIVE' && canDuplicate && (
            <Tooltip title="Copy a day's orders onto a day you choose">
              <Button
                variant="outlined"
                size="small"
                startIcon={<DuplicateIcon />}
                onClick={onDuplicateDay}
              >
                Duplicate Orders
              </Button>
            </Tooltip>
          )}
          {isVet && status === 'ACTIVE' && viewingToday && (
            <Tooltip title="Generate treatment orders from a dictated plan using AI">
              <Button
                variant="contained"
                size="small"
                startIcon={<AIIcon />}
                onClick={onAIGenerate}
                sx={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', '&:hover': { opacity: 0.9 } }}
              >
                AI Generate
              </Button>
            </Tooltip>
          )}
          {isVet && status === 'ACTIVE' && viewingToday && (
            <Button
              variant="outlined"
              size="small"
              startIcon={<HoofIcon />}
              onClick={onAddOrder}
            >
              Add Order
            </Button>
          )}
          {isVet && status === 'ACTIVE' && (
            <Button
              variant="outlined"
              color="warning"
              size="small"
              startIcon={<DischargeIcon />}
              onClick={onDischarge}
            >
              Discharge
            </Button>
          )}
        </Box>
      </Box>
    </Paper>
  );
};

// ─── Discharge Confirm Dialog ─────────────────────────────────────────────────

interface DischargeDialogProps {
  open: boolean;
  horseName: string;
  onClose: () => void;
  onConfirm: () => void;
  loading: boolean;
}

const DischargeDialog: React.FC<DischargeDialogProps> = ({
  open,
  horseName,
  onClose,
  onConfirm,
  loading,
}) => (
  <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
    <DialogTitle>Discharge Patient</DialogTitle>
    <DialogContent>
      <DialogContentText>
        Are you sure you want to discharge <strong>{horseName}</strong>? This will mark the
        treatment sheet as DISCHARGED and close the sheet.
      </DialogContentText>
    </DialogContent>
    <DialogActions>
      <Button onClick={onClose} disabled={loading}>
        Cancel
      </Button>
      <Button
        onClick={onConfirm}
        color="warning"
        variant="contained"
        disabled={loading}
        startIcon={loading ? <CircularProgress size={16} /> : <DischargeIcon />}
      >
        Discharge
      </Button>
    </DialogActions>
  </Dialog>
);

// ─── Main Page ────────────────────────────────────────────────────────────────

const TreatmentSheetPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const [dischargeOpen, setDischargeOpen] = useState(false);
  const [addOrderOpen, setAddOrderOpen] = useState(false);
  const [editingOrder, setEditingOrder] = useState<OrderWithSlots | null>(null);
  const [aiGeneratorOpen, setAiGeneratorOpen] = useState(false);
  // Day ladder — orders are day-scoped; default to today.
  const todayKey = dayKeyOf(new Date());
  const [selectedDayKey, setSelectedDayKey] = useState(todayKey);
  const [duplicateConfirm, setDuplicateConfirm] = useState<{
    sourceDayKey: string;
    targetDayKey: string;
    count: number | null;
  } | null>(null);

  const {
    data: sheet,
    isLoading,
    isError,
    refetch,
  } = useQuery<TreatmentSheet>(
    ['treatment-sheet', id],
    () => treatmentSheetApi.getSheet(id!),
    { enabled: !!id },
  );

  // The stay's running total. Refetched when the grid changes so marking a dose
  // given updates the number on the header without a manual reload.
  const { data: billing } = useQuery<TreatmentSheetBillingSummary>(
    ['sheet-billing', id],
    () => treatmentSheetApi.getBillingSummary(id!),
    { enabled: !!id },
  );

  // Active orders feed the ladder's far end: duplicate-to-date can pre-fill a
  // FUTURE day, and without this the new day's tab wouldn't exist until the
  // clock reached it.
  const { data: sheetOrders = [] } = useQuery(
    ['sheet-orders', id],
    () => treatmentSheetApi.listOrders(id!),
    { enabled: !!id },
  );
  const lastOrderEnd = sheetOrders.reduce<string | null>(
    (max, o) => (o.endAt && (!max || o.endAt > max) ? o.endAt : max),
    null,
  );
  const dayTabs = sheet
    ? buildDayTabs(
        sheet.admitDate,
        new Date(),
        sheet.status === 'ACTIVE' ? null : sheet.dischargeDate,
        lastOrderEnd,
      )
    : [];
  const selectedTab = dayTabs.find((t) => t.dayKey === selectedDayKey) ?? dayTabs[dayTabs.length - 1];
  const viewingToday = selectedTab?.isToday ?? true;

  const duplicateMutation = useMutation(
    ({ sourceDate, targetDate }: { sourceDate: string; targetDate: string }) =>
      treatmentSheetApi.duplicateDay(id!, {
        sourceDate,
        targetDate,
        timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      }),
    {
      onSuccess: (result) => {
        queryClient.invalidateQueries(['admin-grid', id]);
        queryClient.invalidateQueries(['sheet-orders', id]);
        setSelectedDayKey(result.targetDate);
        setDuplicateConfirm(null);
        const skipped = result.skippedExisting.length;
        const targetLabel =
          result.targetDate === todayKey
            ? 'today'
            : format(parseISO(result.targetDate), 'EEE, MMM d');
        enqueueSnackbar(
          `Copied ${result.createdCount} order${result.createdCount === 1 ? '' : 's'} to ${targetLabel}` +
            (skipped ? ` (${skipped} already present)` : ''),
          { variant: result.createdCount > 0 ? 'success' : 'info' },
        );
      },
      onError: (err: any) => {
        setDuplicateConfirm(null);
        enqueueSnackbar(err?.response?.data?.message ?? 'Failed to duplicate the day', { variant: 'error' });
      },
    },
  );

  // Source = the VIEWED day; on today's tab, the most recent prior day (the
  // morning workflow: open sheet -> empty Today -> one click restores the
  // board). The target defaults to the day AFTER the source — which makes the
  // morning flow pre-select today — and is editable in the dialog.
  const openDuplicateConfirm = async () => {
    // STRICTLY-prior days: the ladder can now hold FUTURE tabs (duplicate-to-
    // date pre-fills them), so `!isToday` would grab the future-most tab as
    // the source and break the morning yesterday→today flow.
    const priorTabs = dayTabs.filter((t) => t.dayKey < todayKey);
    const source = viewingToday ? priorTabs[priorTabs.length - 1] : selectedTab;
    if (!source) return;
    setDuplicateConfirm({
      sourceDayKey: source.dayKey,
      targetDayKey: dayKeyOf(addDays(parseISO(source.dayKey), 1)),
      count: null,
    });
    try {
      const grid = await queryClient.fetchQuery(['admin-grid', id, source.dayKey], () =>
        treatmentSheetApi.getAdministrationGrid(id!, {
          windowStart: source.windowStart.toISOString(),
          windowEnd: source.windowEnd.toISOString(),
        }),
      );
      setDuplicateConfirm((prev) =>
        prev && prev.sourceDayKey === source.dayKey ? { ...prev, count: grid.orders.length } : prev,
      );
    } catch {
      /* count stays unknown — the dialog still works */
    }
  };

  const dischargeMutation = useMutation(
    () => treatmentSheetApi.updateSheet(id!, { status: 'DISCHARGED' } as any),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['treatment-sheet', id]);
        queryClient.invalidateQueries(['stalls', 'occupancy']);
        setDischargeOpen(false);
        navigate('/hospital');
      },
    },
  );

  if (isLoading) {
    return (
      <Box sx={{ p: 3 }}>
        <Skeleton height={80} sx={{ mb: 2 }} />
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Skeleton variant="rectangular" height={400} sx={{ flex: '0 0 65%' }} />
          <Skeleton variant="rectangular" height={400} sx={{ flex: 1 }} />
        </Box>
      </Box>
    );
  }

  if (isError || !sheet) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={() => refetch()}>
              Retry
            </Button>
          }
        >
          Failed to load treatment sheet.
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      {/* Sticky header */}
      <PatientHeaderBar
        sheet={sheet}
        billing={billing}
        viewingToday={viewingToday}
        // Strictly-prior days can source the today-tab flow; any non-today
        // viewed tab is its own source. `!isToday` alone would count FUTURE
        // tabs and show the button with nothing valid to copy from.
        canDuplicate={dayTabs.some((t) => t.dayKey < todayKey) || !viewingToday}
        onDischarge={() => setDischargeOpen(true)}
        onAddOrder={() => { setEditingOrder(null); setAddOrderOpen(true); }}
        onAIGenerate={() => setAiGeneratorOpen(true)}
        onDuplicateDay={openDuplicateConfirm}
      />

      {/* Day ladder — one tab per day since admission */}
      {dayTabs.length > 0 && selectedTab && (
        <DayLadder
          tabs={dayTabs}
          selectedDayKey={selectedTab.dayKey}
          onSelect={setSelectedDayKey}
        />
      )}

      {/* Main content */}
      <Box
        sx={{
          display: 'flex',
          flex: 1,
          gap: 2,
          p: 2,
          overflow: 'hidden',
        }}
      >
        {/* Left: Treatment Grid — 65% */}
        <Box sx={{ flex: '0 0 65%', minWidth: 0 }}>
          <TreatmentGrid
            sheetId={sheet.id}
            dayKey={selectedTab?.dayKey ?? todayKey}
            windowStart={selectedTab?.windowStart}
            windowEnd={selectedTab?.windowEnd}
            isToday={viewingToday}
            onEditOrder={(order) => { setEditingOrder(order); setAddOrderOpen(true); }}
          />
        </Box>

        {/* Right: Vitals Panel — 35% */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <VitalsPanel sheetId={sheet.id} />
        </Box>
      </Box>

      <Divider />

      {/* Bottom: Clinical Notes */}
      <Box sx={{ p: 2 }}>
        <ClinicalNotesSection sheetId={sheet.id} />
      </Box>

      {/* Discharge dialog */}
      <DischargeDialog
        open={dischargeOpen}
        horseName={sheet.horse?.name ?? 'this patient'}
        onClose={() => setDischargeOpen(false)}
        onConfirm={() => dischargeMutation.mutate()}
        loading={dischargeMutation.isLoading}
      />

      {/* Add / Edit Order slide-over */}
      <AddOrderSlideOver
        sheetId={sheet.id}
        autoBillLive={billing?.autoBillEnabled}
        open={addOrderOpen}
        editingOrder={editingOrder}
        onClose={() => { setAddOrderOpen(false); setEditingOrder(null); }}
        onAdded={() => {
          setAddOrderOpen(false);
          setEditingOrder(null);
          queryClient.invalidateQueries(['admin-grid', sheet.id]);
        }}
      />

      {/* AI Order Generator */}
      {aiGeneratorOpen && (
        <AIOrderGenerator
          open={aiGeneratorOpen}
          sheet={sheet}
          onClose={() => {
            setAiGeneratorOpen(false);
            queryClient.invalidateQueries(['admin-grid', sheet.id]);
          }}
        />
      )}

      {/* Duplicate confirm — names the source day, target day is chosen here
          (defaults to the day after the source; the morning flow lands on today) */}
      <Dialog
        open={!!duplicateConfirm}
        onClose={() => !duplicateMutation.isLoading && setDuplicateConfirm(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Duplicate Orders</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {duplicateConfirm && (
              <>
                Copy{' '}
                <strong>
                  {duplicateConfirm.count === null
                    ? 'the active orders'
                    : `${duplicateConfirm.count} active order${duplicateConfirm.count === 1 ? '' : 's'}`}
                </strong>{' '}
                from <strong>{format(parseISO(duplicateConfirm.sourceDayKey), 'EEE, MMM d')}</strong> onto
                the day below? Times of day are kept; orders already on that day are skipped. Doses
                whose time has already passed will show as due.
              </>
            )}
          </DialogContentText>
          <TextField
            type="date"
            size="small"
            fullWidth
            label="Duplicate to"
            value={duplicateConfirm?.targetDayKey ?? ''}
            onChange={(e) =>
              setDuplicateConfirm((prev) => (prev ? { ...prev, targetDayKey: e.target.value } : prev))
            }
            InputLabelProps={{ shrink: true }}
            inputProps={{
              // Floor: the admit day (a pre-admit target would create orders no
              // ladder tab can ever show). Ceiling: a month out — generous for
              // prepping tomorrow, blocks a typo'd far-future year.
              min: sheet ? dayKeyOf(parseISO(sheet.admitDate)) : undefined,
              max: dayKeyOf(addDays(new Date(), 31)),
            }}
            error={!!duplicateConfirm && duplicateConfirm.targetDayKey === duplicateConfirm.sourceDayKey}
            helperText={
              duplicateConfirm && duplicateConfirm.targetDayKey === duplicateConfirm.sourceDayKey
                ? 'Target must be a different day than the source'
                : undefined
            }
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDuplicateConfirm(null)} disabled={duplicateMutation.isLoading}>
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={
              duplicateMutation.isLoading ||
              duplicateConfirm?.count === 0 ||
              !duplicateConfirm?.targetDayKey ||
              duplicateConfirm?.targetDayKey === duplicateConfirm?.sourceDayKey
            }
            startIcon={duplicateMutation.isLoading ? <CircularProgress size={16} /> : <DuplicateIcon />}
            onClick={() =>
              duplicateConfirm &&
              duplicateMutation.mutate({
                sourceDate: duplicateConfirm.sourceDayKey,
                targetDate: duplicateConfirm.targetDayKey,
              })
            }
          >
            Duplicate
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TreatmentSheetPage;
